import { fetch } from '@nackle/umbrella';
import { kadhavuExpUrl } from 'endpoints';
import { startLoading, endLoading, setError, setAlertMessage } from './app-dux';
import { getString } from '@nackle/intl-tools';
import { PASSWORD_REPEAT_LIMIT } from '../constants';

/*******************************************************************************
 * Initial state
 */
const initialState = {
    passwordRules: {
        'minLength': 4,
        'complexityRequirement': 1,
        'complexity': [
            'isLowerCaseRequired',
            'isUpperCaseRequired'
        ]
    }
};

/*******************************************************************************
 * Non-async action types
 */
export const SET_PASSWORD_RULES = 'state/set_password_rules';
export const SET_LOGGED_IN_PERSON_DETAILS = 'state/set_logged_in_person_details';

/*******************************************************************************
 * Reducer
 */
export default function passwordReducer( state = initialState, action ) {
    switch ( action.type ) {
        case SET_PASSWORD_RULES: {
            return {
                ...state,
                passwordRules: action.passwordRules
            };
        }
        case SET_LOGGED_IN_PERSON_DETAILS: {
            return {
                ...state,
                personDetails: action.personDetails,
                oldPassword: action.password
            };
        }
        default:
            return state;
    }
}

/*******************************************************************************
 * Action creators
 */

export const setLoggedInPersonDetails = ( personDetails, password ) => {
    return {
        type: SET_LOGGED_IN_PERSON_DETAILS,
        personDetails,
        password
    };
};

export const setPasswordRules = ( passwordRules ) => {
    return {
        type: SET_PASSWORD_RULES,
        passwordRules
    };
};

export const getPasswordRules = () => async( dispatch, getState ) => {
    try {
        const res = await fetch( `${ kadhavuExpUrl }/getpasswordRules`, {
            method: 'GET',
            headers: { 'content-type': 'application/json' }
        } );

        const data = await res.json();

        if ( data.errors ) {
            throw data.errors;
        }

        if ( data ) {
            delete data.success;
            dispatch( setPasswordRules( data ) );
        } else {
            dispatch( setError( [ { code: 'errors.content.generic' } ] ) );
        }
    } catch ( error ) {
        console.error( 'error while getting passwordRules:', JSON.stringify( error ) );
        dispatch( setError( [ { code: 'errors.content.generic' } ] ) );
    }
};

export const updatePassword = ( personId, oldPassword, newPassword ) => async ( dispatch, getState ) => {
    const state = getState();
    try {
        dispatch( startLoading() );

        if ( personId ) {
            const res = await fetch( `${ kadhavuExpUrl }/updatePassword`, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify( {
                    personId: personId,
                    oldPassword: oldPassword,
                    newPassword: newPassword,
                    locale: state.app.userSelectedLocale || ''
                } )
            } );

            const data = await res.json();

            if ( data && !data.errors ) {
                const successMessage = getString( 'updatePassword.alert.successMessage' );
                dispatch( setAlertMessage( successMessage, 'success', getString( 'updatePassword.alert.successMessageHeader' ) ) );

                return true;
            } else if ( data.errors ) {
                const errors = [];
                data.errors.forEach( error => {
                    if ( error.code === 'kadhavu.error.invalidAccount' ) {
                        errors.push( { code: 'setPassword.error.invalidAccount' } );
                    } else if ( error.code === 'kadhavu.error.invalidLink' ) {
                        errors.push( { code: 'setPassword.error.invalidLink' } );
                    } else if ( error.code === 'kadhavu.error.passwordRepeatLimit' ) {
                        errors.push( { code: 'updatePassword.error.passwordRepeatLimit', data: { passwordRepeatLimit: PASSWORD_REPEAT_LIMIT } } );
                    } else if ( error.code === 'kadhavu.error.terminatedWithNoPoints' ) {
                        errors.push( { code: 'kadhavu.error.terminatedWithNoPoints' } );
                    } else {
                        errors.push( { code: 'setPassword.error.unknownError' } );
                    }
                } );

                dispatch( setError( errors ) );
            }
            dispatch( endLoading() );
        } else {
            dispatch( setError( [ { code: 'setPassword.error.unknownError' } ] ) );
        }
    } catch ( err ) {
        console.log( 'error when updating password', err );
        dispatch( setError( [ { code: 'setPassword.error.unknownError' } ] ) );
    } finally {
        dispatch( endLoading() );
    }
};